import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  Button, FormControl, FormLabel, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, Text, Textarea,
} from "@chakra-ui/react";
import { useWith2FA } from "tools/twoFactor";
import { buildApproveConfig, rejectConfiscateRequest } from "./confiscationApi";

/**
 * Approves and executes a confiscation request (2FA + 4-eyes).
 * @param {Object} args - request, reason, runWith2FA, setLoading, onClose, onDone
 * @returns {Promise<void>}
 */
async function submitApprove({ request, reason, runWith2FA, setLoading, onClose, onDone }) {
  if (!reason.trim()) { toast.error("Le motif de l'approbateur est obligatoire"); return; }
  setLoading(true);
  try {
    await runWith2FA(buildApproveConfig(request, reason.trim()));
    toast.success("Confiscation approuvée et exécutée");
    onDone?.();
    onClose();
  } catch (error) {
    if (error?.message !== "2fa_cancelled") {
      toast.error(typeof error?.response?.data === "string" ? error.response.data : "Échec de l'approbation");
    }
  } finally {
    setLoading(false);
  }
}

/**
 * Rejects a pending confiscation request (no 2FA).
 * @param {Object} args - request, reason, setLoading, onClose, onDone
 * @returns {Promise<void>}
 */
async function submitReject({ request, reason, setLoading, onClose, onDone }) {
  setLoading(true);
  try {
    await rejectConfiscateRequest(request.id, reason.trim());
    toast.success("Demande rejetée");
    onDone?.();
    onClose();
  } catch (error) {
    toast.error(typeof error?.response?.data === "string" ? error.response.data : "Échec du rejet");
  } finally {
    setLoading(false);
  }
}

/**
 * Dialog to approve a confiscation request. Requires the approver's reason and
 * triggers the 2FA flow; the API enforces that the approver differs from the
 * requester (4-eyes).
 * @param {Object} props - open, request, onClose, onDone
 * @returns {React.ReactElement|null}
 */
function ApproveConfiscateDialog({ open, request, onClose, onDone }) {
  const runWith2FA = useWith2FA();
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  if (!request) return null;

  const onSubmit = () => submitApprove({ request, reason, runWith2FA, setLoading, onClose, onDone });
  return (
    <Modal isOpen={open} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="green.500">Approuver la confiscation</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="secondaryGray.600" _dark={{ color: "secondaryGray.400" }} mb="14px">
            Vous allez exécuter la confiscation (solde remis à zéro, wallet gelé, utilisateur notifié).
            Vous devez être un super admin différent du demandeur.
          </Text>
          <FormControl>
            <FormLabel fontSize="sm">Motif de l'approbateur (obligatoire)</FormLabel>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
          </FormControl>
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button colorScheme="green" onClick={onSubmit} isDisabled={loading || !reason.trim()} isLoading={loading}>
            Approuver
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * Dialog to reject a confiscation request (optional reason).
 * @param {Object} props - open, request, onClose, onDone
 * @returns {React.ReactElement|null}
 */
function RejectConfiscateDialog({ open, request, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  if (!request) return null;

  const onSubmit = () => submitReject({ request, reason, setLoading, onClose, onDone });
  return (
    <Modal isOpen={open} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="red.500">Rejeter la demande</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="secondaryGray.600" _dark={{ color: "secondaryGray.400" }} mb="14px">
            La demande sera marquée rejetée. Aucun solde n'est touché.
          </Text>
          <FormControl>
            <FormLabel fontSize="sm">Motif (optionnel)</FormLabel>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
          </FormControl>
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button colorScheme="red" onClick={onSubmit} isDisabled={loading} isLoading={loading}>Rejeter</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export { ApproveConfiscateDialog, RejectConfiscateDialog };
